import { ApexOptions } from "apexcharts";
import { useMemo } from "react";
import LazyApexChart from '../../../client/components/LazyApexChart';
import { chartColors } from "../../../dashboard/widgets/chart/chartColors";
import { useThemeId } from "../../../client/components/ui/card-section-header";

type Props = {
  dau: { day: string; count: number }[];
};

const UserActivityChart = ({ dau }: Props) => {
  const themeId = useThemeId();
  const options: ApexOptions = useMemo(
    () => {
      const c = chartColors();
      return {
      chart: {
        type: "area",
        height: 220,
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: "'Red Hat Text', sans-serif",
      },
      colors: [c.accent],
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 2 },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.4,
          opacityTo: 0.05,
          stops: [0, 90, 100],
        },
      },
      grid: {
        borderColor: c.grid,
        strokeDashArray: 3,
      },
      xaxis: {
        categories: dau.map((d) => d.day),
        labels: {
          style: { colors: c.axis, fontSize: "11px" },
          formatter: (v: string) => v?.slice(5),
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: { style: { colors: c.axis, fontSize: "11px" } },
        min: 0,
        forceNiceScale: true,
      },
      tooltip: {
        theme: "dark",
        x: { format: "yyyy-MM-dd" },
      },
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dau, themeId],
  );

  const series = useMemo(
    () => [{ name: "Active users", data: dau.map((d) => d.count) }],
    [dau],
  );

  return (
    <div className="border-border bg-card rounded-sm border p-5 shadow">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-foreground text-base font-semibold">
            Daily Active Users
          </h3>
          <p className="text-muted-foreground text-xs">
            Last 30 days (UTC)
          </p>
        </div>
        <div className="text-right">
          <div className="text-foreground text-2xl font-bold">
            {dau[dau.length - 1]?.count ?? 0}
          </div>
          <div className="text-muted-foreground text-xs">today</div>
        </div>
      </div>
      <LazyApexChart options={options} series={series} type="area" height={220} />
    </div>
  );
};

export default UserActivityChart;
